// BRK-S106 — how a CAPTURED bond value reads once nobody is editing it.
//
// The bond flow's controls know how to render a field for input; a filed order
// is a record, and a record needs the other half: one captured value → one
// display string. This is that half, and it is deliberately pure — the
// submitted-order view is presentational, so the formatting is unit-testable
// without mounting anything.
//
// PARITY, NOT REUSE. PMS's admin bond detail formats the same values through
// its own `lib/bond-value-display.ts` (PMS-S236). The two repos share no code,
// so this reimplements it in this portal's idiom and the OUTPUT is pinned as a
// convention by `test/lib/bondValueDisplay.test.ts` — because the thing that
// actually matters is that a broker and an underwriter reading the same bond
// order see the same string, not that the code looks alike.
//
// The hints are the CONFIGURATION's own (`type` from the field or final-step
// definition, the SUR-S041 vocabulary), so the display side does not guess from
// the value alone — except for addresses, whose ONE canonical composite
// ({street, addressLine2?, city, state, zip}) is recognisable on sight.
//
// NO LEGACY VALUE SHAPES ARE TOLERATED HERE (normalization Phase 3, BRK-S110).
// Not `{line1,…}` addresses, not `[{line1,…}]` or `[{number}]` block arrays, not
// numerics stored as strings. Each is a shape the canonical contract cannot
// produce on a read surface, and the seam's own representation guard already
// fails them — so rendering them here was one file quietly repairing what the
// file beside it called impossible. They degrade to visible JSON or to the raw
// value (NN #7) rather than earning a second first-class code path, and sandbox
// data predating normalization gets wiped and re-seeded (Aaron, 2026-08-14).

import { formatAddress, type AddressParts } from "@/lib/address";
import { withBondOptionLabels } from "@/lib/bondOptionLabels";
import { formatE164ForDisplay } from "@/lib/phone";
import type { BondFieldOption, BondFieldType } from "@/lib/brokerBonds";

export interface BondValueHints {
  /** Widened past `BondFieldType` on purpose: a pre-SUR-S041 config snapshot
   *  may name a type this build has never heard of, and an unknown hint must
   *  degrade to plain text rather than fail to compile against a fixture. */
  type?: BondFieldType | string;
  /** The field's options from the resolved config, when it has any — how an
   *  option-id value renders as its LABEL ("LLC", never 1). General, not an
   *  entityType special case: any field carrying pairs resolves the same way
   *  (BRK-S107). */
  options?: BondFieldOption[];
}

/**
 * m/d/yyyy, no leading zeros — the D-10 display convention. DISPLAY only: the
 * wire and storage stay ISO `YYYY-MM-DD`.
 *
 * Parsed TEXTUALLY from the ISO string's own date part rather than through
 * `Date`, so a date-only value can never shift a day under the viewer's
 * timezone — the classic way a bid date renders as the day before the bid.
 * An unparseable value comes back untouched: showing what was stored beats
 * inventing a date.
 */
export function formatBondDate(value: string): string {
  const [datePart] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return String(value);
  return `${month}/${day}/${year}`;
}

/**
 * `$250,000` — the D-10 currency convention, whole dollars with a thousands
 * separator. DISPLAY only: the wire and storage stay a plain number, because a
 * formatted string stops being summable.
 */
export function formatBondCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * A PREAPPROVAL limit — the single-bond and aggregate program figures on the
 * indication (BRK-S133) and on BRK-S134's standing banner.
 *
 * NOT `formatBondCurrency`, and the difference is the one thing this function
 * exists for: **cents are kept when the figure has any**, rather than rounded
 * away. A captured bond value is a whole-dollar amount a broker typed, so
 * rounding it is harmless; a program limit is a figure SIGNAL GRANTED, and a
 * silently rounded figure is a changed figure. `$250,000.50` must not read as
 * `$250,001` on the screen a broker quotes from.
 *
 * PARITY, NOT REUSE — the same rule this file's header states, and here it has
 * a named counterpart: PMS's `lib/preapproval-display.ts`
 * `formatPreapprovalMoney` (PMS-S303) formats these three figures for the admin
 * panel and for the Program Indication letter itself. The output is pinned as a
 * convention by `test/lib/bondValueDisplay.test.ts`, because what matters is
 * that a broker reading the portal, an underwriter reading the admin screen and
 * anyone reading the PDF see the SAME string — not that the code looks alike.
 */
export function formatPreapprovalMoney(value: number): string {
  const abs = Math.abs(value).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${abs}` : `$${abs}`;
}

interface AddressLikeValue {
  street?: unknown;
  addressLine2?: unknown;
  city?: unknown;
  state?: unknown;
  zip?: unknown;
}

/**
 * A captured address in the ONE canonical spelling — `{street, addressLine2?,
 * city, state, zip}` — mapped onto the shared formatter's shape, or null when
 * the object is not RECOGNISABLY an address (it is missing one of the four
 * required lines).
 *
 * The caller then falls back to JSON rather than printing a half-address that
 * looks complete: an underwriter who reads "1 Via Sonoma, , CA " cannot tell
 * whether the city is missing from the record or from the renderer.
 *
 * NO `line1` / `line2` TOLERANCE (normalization Phase 3, BRK-S110). This read
 * both spellings, on the grounds that they "predate the type hints". They are
 * not two spellings of a read value: `line1` is the WRITE representation on the
 * path into discovery's vendor adapter, and the seam's own representation guard
 * already FAILS any address on a read surface carrying it. Accepting it here
 * meant one file rejecting the shape as impossible while the file beside it
 * rendered it prettily — and a value that cannot reach a read surface needs no
 * display rule. A `{line1}` object now falls through to the JSON fallback, the
 * same way a Lendflow BLOCK-shaped array already does.
 */
function asAddressParts(value: unknown): AddressParts | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const a = value as AddressLikeValue;
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v !== "" ? v : undefined;
  const street = str(a.street);
  const city = str(a.city);
  const state = str(a.state);
  const zip = str(a.zip);
  if (!street || !city || !state || !zip) return null;
  return {
    street,
    addressLine2: str(a.addressLine2) ?? null,
    city,
    state,
    zip,
  };
}

/**
 * A stored phone value, spelled the way a person says it. The wire value is
 * E.164 (`+12816871161` — PMS normalizes on save), and only a value that is
 * recognisably a US number is reformatted: anything else comes back untouched,
 * because mangling a number is worse than showing the stored form.
 */
function formatPhoneDisplay(value: string): string {
  const digits = value.replace(/\D/g, "").replace(/^1/, "");
  return digits.length === 10 ? formatE164ForDisplay(value) : value;
}

/**
 * The option (if any) whose value matches the captured one. Ids are numeric on
 * the Lendflow side and may be stored as number OR string, so the match is by
 * string form. Plain-string options need no lookup — the value IS the label —
 * and a value matching no option falls through to render as itself: the raw id
 * is the honest answer when the config no longer names it.
 *
 * Except where this client supplies a display label the configuration does not
 * (BRK-S173): `withBondOptionLabels` pairs those up first, so a stored `Fed`
 * reads "Federal" on the profile, the record and the confirmation. The wire
 * value is untouched, and a configuration-supplied label always wins — see
 * `bondOptionLabels.ts`. It is applied HERE rather than at the three call
 * sites so the read surfaces cannot disagree about one option's wording.
 */
function optionLabel(
  value: unknown,
  configOptions: BondFieldOption[] | undefined,
): string | null {
  const options = withBondOptionLabels(configOptions);
  if (!options || (typeof value !== "string" && typeof value !== "number")) {
    return null;
  }
  for (const option of options) {
    if (typeof option === "object" && String(option.value) === String(value)) {
      return option.label;
    }
  }
  return null;
}

// Deliberately NO tolerance for Lendflow BLOCK-shaped values (addresses as
// `[{line1,…}]` arrays, phones as `[{number}]` arrays): the canonical stored
// shape is the composite, SUR-S061 normalizes it upstream, and a sandbox order
// predating that gets wiped and re-seeded rather than accommodated (Aaron,
// 2026-08-14). A block-shaped value falls through to the JSON fallback below.

/**
 * One captured value → its display string, or **null** for "unanswered".
 *
 * Null rather than an em dash so the caller decides: the submitted-order view
 * OMITS an unanswered row, because a record padded with dashes for every
 * optional field nobody filled in reads as a form, not as a record.
 *
 * Note what counts as unanswered: `undefined`, `null`, and `""` only. **Zero
 * and false are answers** — 0% ownership and an unticked attestation are things
 * the broker said.
 */
/**
 * The document references a `files` value carries, or null when the value is
 * not one.
 *
 * A PREDICATE rather than a cast, mirroring PMS's `documentReferences`: a
 * caller holding an unknown bag value asks this, and `null` is the honest
 * answer for "that field holds something else" — including a legacy capture,
 * since stored orders are never rewritten. Every entry must parse, so a mixed
 * array is not a document list.
 */
function documentReferences(value: unknown): { filename: string }[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const refs: { filename: string }[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null) return null;
    const ref = raw as Record<string, unknown>;
    if (
      typeof ref.documentId !== "string" ||
      typeof ref.filename !== "string" ||
      typeof ref.contentType !== "string" ||
      typeof ref.byteSize !== "number"
    ) {
      return null;
    }
    refs.push({ filename: ref.filename });
  }
  return refs;
}

export function formatBondValue(
  value: unknown,
  hints: BondValueHints = {},
): string | null {
  if (value === undefined || value === null || value === "") return null;

  // A `files` value (BRK-S145 / PMS-S334): the references PMS handed back for
  // what the broker attached. Recognised ON SIGHT rather than gated on
  // `hints.type === "files"` — the same shape means the same thing wherever it
  // turns up, and a record rendering a SNAPSHOT that predates the hint would
  // otherwise print raw JSON at a broker. Which it did: this was found by
  // walking a live order, where the terms record read
  // `[{"documentId":"e33781ab-…","filename":"brk-s145-live-walk.pdf",…}]`.
  //
  // FILENAMES ONLY, mirroring PMS's own rule verbatim. The document id is a
  // locator this row cannot act on (the Documents section is where a file is
  // downloaded), and a content type and byte count beside every name would bury
  // the one thing a reader wants from a captured-values table: what was
  // attached.
  const files = documentReferences(value);
  if (files) return files.map((ref) => ref.filename).join("; ");

  // An ARRAY is the CONTRACT, not an accident (mirroring PMS's own rule): a
  // `block[].field` path carries one entry per array-block element — two owners,
  // two first names — and a Lendflow LIST field is arity-faithful. Each element
  // formats under the same hints; the join keeps the record at one row per
  // configured field.
  //
  // Adopted here alongside the `files` rule above, because it is the same
  // defect one level out: BRK had no array rule at all, so ANY array value fell
  // through to `JSON.stringify`. This module's whole premise is that "a broker
  // and an underwriter looking at the same order see the same string", and raw
  // JSON on one side of that is a parity break that was already shipped.
  if (Array.isArray(value)) {
    const parts = value
      .map((element) => formatBondValue(element, hints))
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join("; ") : null;
  }

  if (typeof value === "boolean" || hints.type === "checkbox") {
    return value ? "Yes" : "No";
  }

  const label = optionLabel(value, hints.options);
  if (label !== null) return label;

  const address = asAddressParts(value);
  if (address) return formatAddress(address);

  if (hints.type === "phone" && typeof value === "string") {
    return formatPhoneDisplay(value);
  }

  // A PLAIN NUMBER only — no coercion (normalization Phase 3, BRK-S110). This
  // used to `Number()` the value, tolerating the numeric-as-string bags
  // pre-BRK-S091 drafts produced. D-10's display/wire split runs BOTH ways, and
  // the seam's representation guard already fails a currency value that is not
  // a number — so coercing here quietly repaired ON SCREEN the exact bag that
  // guard exists to reject, which is how a value stops being summable without
  // anyone noticing. A string renders as itself instead (NN #7).
  if (hints.type === "currency" && typeof value === "number") {
    if (Number.isFinite(value)) return formatBondCurrency(value);
  }

  if (hints.type === "date" && typeof value === "string") {
    return formatBondDate(value);
  }

  // Never silently dropped: an object with no display of its own is better read
  // as its JSON than not read at all (NN #7 — degrade, don't disappear).
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
